import {
  createReducer,
  ActionType,
  createAsyncAction,
  createAction,
} from "typesafe-actions";
import {
  CompanySaveRequest,
  ResultOfCompanySaveResponse,
  CompanyDto,
  CompaniesListDto,
  GetCompaniesListQuery,
  ResultOfBoolean,
  CompanyContactPersonDto,
  ResultOfCompanyContactPersonDto,
  ResultOfInteger,
} from "Api/Api";

export type CompaniesState = {
  companies: CompaniesListDto;
  companyDetail: CompanyDto | null;
  isLoadingCompanyDetail: boolean;
  isCompanyDetailSaving: boolean;
  isLoading: boolean;
  isDeleting: boolean;
  errorCode: string | null;
  deletingCompanyContactID: number | null;
  editedCompanyContactPerson: CompanyContactPersonDto | null;
};

export const getCompaniesAsync = createAsyncAction(
  "@company/GET_COMPANIES_REQUEST",
  "@company/GET_COMPANIES_SUCCESS",
  "@company/GET_COMPANIES_FAILURE",
)<GetCompaniesListQuery, CompaniesListDto, Error>();

export const getCompaniesDetailAsync = createAsyncAction(
  "@company/GET_COMPANY_DETAIL_REQUEST",
  "@company/GET_COMPANY_DETAIL_SUCCESS",
  "@company/GET_COMPANY_DETAIL_FAILURE",
)<number, CompanyDto, Error>();

export const resetCompanyDetail = createAction(
  "@company/RESET_COMPANY_DETAIL",
)<void>();

export const openCompanyContactPersonModal = createAction(
  "@company/OPEN_COMPANY_CONTACT_PERSON_MODAL",
)<{ companyID: number; editedPerson: CompanyContactPersonDto | null }>();

export const closeCompanyContactPersonModal = createAction(
  "@company/CLOSE_COMPANY_CONTACT_PERSON_MODAL",
)<void>();

export const openCompanyContactPersonDeleteModal = createAction(
  "@company/OPEN_COMPANY_CONTACT_PERSON_DELETE_MODAL",
)<number>();

export const closeCompanyContactPersonDeleteModal = createAction(
  "@company/CLOSE_COMPANY_CONTACT_PERSON_DELETE_MODAL",
)();

export const deleteCompaniesAsync = createAsyncAction(
  "@company/DELETE_COMPANY_REQUEST",
  "@company/DELETE_COMPANY_SUCCESS",
  "@company/DELETE_COMPANY_FAILURE",
)<number, { result: ResultOfBoolean; id: number }, Error>();

export const deleteCompanyContactAsync = createAsyncAction(
  "@company/DELETE_CONTACT_COMPANY_REQUEST",
  "@company/DELETE_CONTACT_COMPANY_SUCCESS",
  "@company/DELETE_CONTACT_COMPANY_FAILURE",
)<number, ResultOfInteger, Error>();

export const saveCompanyAsync = createAsyncAction(
  "@company/SAVE_COMPANY_REQUEST",
  "@company/SAVE_COMPANY_SUCCESS",
  "@company/SAVE_COMPANY_FAILURE",
)<CompanySaveRequest, ResultOfCompanySaveResponse, Error>();

export const saveCompanyContactPersonAsync = createAsyncAction(
  "@company/SAVE_COMPANY_CONTACT_PERSON_REQUEST",
  "@company/SAVE_COMPANY_CONTACT_PERSON_SUCCESS",
  "@company/SAVE_COMPANY_CONTACT_PERSON_FAILURE",
)<CompanyContactPersonDto, ResultOfCompanyContactPersonDto, Error>();

export type CompanyAction =
  | ActionType<typeof getCompaniesAsync>
  | ActionType<typeof deleteCompaniesAsync>
  | ActionType<typeof getCompaniesDetailAsync>
  | ActionType<typeof resetCompanyDetail>
  | ActionType<typeof saveCompanyAsync>
  | ActionType<typeof openCompanyContactPersonModal>
  | ActionType<typeof closeCompanyContactPersonModal>
  | ActionType<typeof saveCompanyContactPersonAsync>
  | ActionType<typeof deleteCompanyContactAsync>
  | ActionType<typeof openCompanyContactPersonDeleteModal>
  | ActionType<typeof closeCompanyContactPersonDeleteModal>;

export const companyReducer = createReducer<CompaniesState, CompanyAction>({
  companies: {
    count: 0,
    companies: [],
  },
  companyDetail: null,
  isLoadingCompanyDetail: false,
  isCompanyDetailSaving: false,
  isLoading: false,
  isDeleting: false,
  errorCode: null,
  editedCompanyContactPerson: null,
  deletingCompanyContactID: null,
})
  .handleAction(getCompaniesAsync.request, state => {
    return { ...state, isLoading: true };
  })
  .handleAction(getCompaniesAsync.success, (state, action) => {
    return {
      ...state,
      companies: action.payload,
      errorCode: null,
      isLoading: false,
    };
  })
  .handleAction(getCompaniesAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
    };
  })

  .handleAction(getCompaniesDetailAsync.request, state => {
    return {
      ...state,
      isLoadingCompanyDetail: true,
      editedCompanyContactPerson: null,
    };
  })
  .handleAction(getCompaniesDetailAsync.success, (state, action) => {
    return {
      ...state,
      companyDetail: action.payload,
      isLoadingCompanyDetail: false,
    };
  })
  .handleAction(getCompaniesDetailAsync.failure, (state, action) => {
    return {
      ...state,
      isLoadingCompanyDetail: false,
      errorCode: action.payload.name,
    };
  })

  .handleAction(resetCompanyDetail, state => {
    return {
      ...state,
      companyDetail: null,
    };
  })

  .handleAction(saveCompanyAsync.request, state => {
    return {
      ...state,
      isCompanyDetailSaving: true,
    };
  })
  .handleAction(saveCompanyAsync.success, (state, action) => {
    return {
      ...state,
      isCompanyDetailSaving: false,
      companyDetail: action.payload.data?.company ?? null,
    };
  })
  .handleAction(saveCompanyAsync.failure, state => {
    return {
      ...state,
      isCompanyDetailSaving: false,
    };
  })

  .handleAction(deleteCompaniesAsync.request, state => {
    return {
      ...state,
      isDeleting: true,
    };
  })
  .handleAction(deleteCompaniesAsync.success, (state, action) => {
    if (!action.payload.result.isValid) {
      return {
        ...state,
      };
    }

    let companies = state.companies;
    companies.companies = companies.companies.filter(
      e => e.companyID !== action.payload.id,
    );
    return {
      ...state,
      isDeleting: false,
      companyDetail: null,
      companies: companies,
    };
  })
  .handleAction(deleteCompaniesAsync.failure, (state, action) => {
    return {
      ...state,
      isDeleting: false,
      errorCode: action.payload.name,
    };
  })
  .handleAction(openCompanyContactPersonModal, (state, action) => {
    let person =
      action.payload.editedPerson === null
        ? {
            companyID: action.payload.companyID,
            firstName: "",
            lastName: "",
            positionName: "",
            positionID: 0,
            companyContactPersonID: 0,
            charges: [],
          }
        : action.payload.editedPerson;

    return {
      ...state,
      editedCompanyContactPerson: person,
    };
  })
  .handleAction(closeCompanyContactPersonModal, state => {
    return {
      ...state,
      editedCompanyContactPerson: null,
    };
  })
  .handleAction(saveCompanyContactPersonAsync.request, state => {
    return {
      ...state,
    };
  })
  .handleAction(saveCompanyContactPersonAsync.success, (state, action) => {
    if (!state.companyDetail || !action.payload.data) {
      return {
        ...state,
      };
    }

    const existingIndex = state.companyDetail.contacts.findIndex(
      e =>
        e.companyContactPersonID ===
        action.payload.data?.companyContactPersonID,
    );

    if (existingIndex > -1) {
      state.companyDetail.contacts[existingIndex] = {
        ...action.payload.data,
      };
    } else {
      state.companyDetail.contacts.push(action.payload.data);
    }

    state.companyDetail.contacts.sort((a, b) =>
      `${a.firstName}${a.lastName}`.localeCompare(
        `${b.firstName}${b.lastName}`,
      ),
    );

    return {
      ...state,
      editedCompanyContactPerson: null,
      companyDetail: {
        ...state.companyDetail,
        contacts: state.companyDetail.contacts,
      },
    };
  })
  .handleAction(saveCompanyContactPersonAsync.failure, (state, action) => {
    return {
      ...state,
    };
  })
  .handleAction(deleteCompanyContactAsync.success, (state, action) => {
    if (!state.companyDetail) {
      return {
        ...state,
        deletingCompanyContactID: null,
      };
    }

    let contacts = state.companyDetail.contacts;
    contacts = contacts.filter(
      e => e.companyContactPersonID !== state.deletingCompanyContactID,
    );

    return {
      ...state,
      deletingCompanyContactID: null,
      companyDetail: {
        ...state.companyDetail,
        contacts: contacts,
      },
    };
  })
  .handleAction(openCompanyContactPersonDeleteModal, (state, action) => {
    return {
      ...state,
      deletingCompanyContactID: action.payload,
    };
  })
  .handleAction(closeCompanyContactPersonDeleteModal, state => {
    return {
      ...state,
      deletingCompanyContactID: null,
    };
  });
